import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useCards, useBlockCard, useUnblockCard } from '../../../hooks/useCards';
import type { CardStatus, CardType } from '../../../types/cards';

const statusColors: Record<CardStatus, 'success' | 'primary' | 'default' | 'error'> = {
  PENDING_ACTIVATION: 'primary',
  ACTIVE: 'success',
  BLOCKED: 'error',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

const statusLabels: Record<CardStatus, string> = {
  PENDING_ACTIVATION: 'Pendente',
  ACTIVE: 'Ativo',
  BLOCKED: 'Bloqueado',
  CANCELLED: 'Cancelado',
  EXPIRED: 'Expirado',
};

const typeLabels: Record<CardType, string> = {
  PREPAID: 'Pré-pago',
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
};

export function CardManagement() {
  const [statusFilter, setStatusFilter] = useState<CardStatus | undefined>();
  const [typeFilter, setTypeFilter] = useState<CardType | undefined>();
  const { data: cardsData, isLoading } = useCards({ status: statusFilter, type: typeFilter });
  const blockCard = useBlockCard();
  const unblockCard = useUnblockCard();

  const handleBlock = async (cardId: string) => {
    const reason = prompt('Motivo do bloqueio:');
    if (reason) {
      await blockCard.mutateAsync({ cardId, reason });
    }
  };

  const handleUnblock = async (cardId: string) => {
    await unblockCard.mutateAsync(cardId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Gestão de Cartões</h1>
        <p className="text-gray-500">Gerencie os cartões emitidos</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={!statusFilter ? 'primary' : 'secondary'}
                  onClick={() => setStatusFilter(undefined)}
                >
                  Todos
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === 'ACTIVE' ? 'primary' : 'secondary'}
                  onClick={() => setStatusFilter('ACTIVE')}
                >
                  Ativos
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === 'BLOCKED' ? 'primary' : 'secondary'}
                  onClick={() => setStatusFilter('BLOCKED')}
                >
                  Bloqueados
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === 'PENDING_ACTIVATION' ? 'primary' : 'secondary'}
                  onClick={() => setStatusFilter('PENDING_ACTIVATION')}
                >
                  Pendentes
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={!typeFilter ? 'primary' : 'secondary'}
                  onClick={() => setTypeFilter(undefined)}
                >
                  Todos
                </Button>
                <Button
                  size="sm"
                  variant={typeFilter === 'PREPAID' ? 'primary' : 'secondary'}
                  onClick={() => setTypeFilter('PREPAID')}
                >
                  Pré-pago
                </Button>
                <Button
                  size="sm"
                  variant={typeFilter === 'CREDIT' ? 'primary' : 'secondary'}
                  onClick={() => setTypeFilter('CREDIT')}
                >
                  Crédito
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Cartões ({cardsData?.total || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cardsData?.cards && cardsData.cards.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Cartão</th>
                    <th className="text-left py-3 px-4">Tipo</th>
                    <th className="text-left py-3 px-4">Bandeira</th>
                    <th className="text-left py-3 px-4">Limite</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {cardsData.cards.map((card) => (
                    <tr key={card.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{card.panMasked}</p>
                          <p className="text-sm text-gray-500">{card.cardholderName}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="default">
                          {typeLabels[card.type]}
                        </Badge>
                        {card.isPhysical && (
                          <Badge variant="primary" className="ml-1">Físico</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">{card.brand}</td>
                      <td className="py-3 px-4">
                        {card.type === 'CREDIT' ? (
                          <div>
                            <p className="font-medium">{formatCurrency(card.creditLimit || 0)}</p>
                            <p className="text-sm text-gray-500">
                              Disponível: {formatCurrency(card.availableLimit || 0)}
                            </p>
                          </div>
                        ) : (
                          <p>{formatCurrency(card.dailyLimit)}/dia</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={statusColors[card.status]}>
                          {statusLabels[card.status]}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {card.status === 'ACTIVE' && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleBlock(card.id)}
                              disabled={blockCard.isPending}
                            >
                              Bloquear
                            </Button>
                          )}
                          {card.status === 'BLOCKED' && (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleUnblock(card.id)}
                              disabled={unblockCard.isPending}
                            >
                              Desbloquear
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Nenhum cartão encontrado
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CardManagement;
